import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import { getUnseenCount } from "@/lib/brokerNotifications";

// How often the bell re-polls the unseen count. No WS/SSE — a short interval poll
// is the portal's idiom (useRunDetails / TearSheetAutoRefresh). Exported for tests.
export const NOTIFICATIONS_POLL_MS = 60_000;

// The top-bar notification bell (BRK-S157 / PMS-S353): a link to /notifications
// with a real UNSEEN-count badge. Gated on notification:view — hidden entirely for
// a broker without it. Polls the count on an interval AND re-reads on route change,
// so opening the inbox (which marks rows seen server-side) clears the badge on
// return without waiting for the next tick. Fail-soft: a failed read keeps the last
// value and never white-screens (#7).
export function NotificationBell() {
  const { identity, authedRequest } = useAuth();
  const canView = hasCapability(identity, "notification:view");
  const [count, setCount] = useState(0);
  const pathname = useLocation().pathname;

  useEffect(() => {
    if (!canView) return;
    let active = true;
    const poll = () => {
      getUnseenCount(authedRequest)
        .then((c) => {
          if (active) setCount(c);
        })
        .catch(() => {
          /* fail soft — keep the last count */
        });
    };
    poll();
    const timer = setInterval(poll, NOTIFICATIONS_POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [canView, authedRequest, pathname]);

  if (!canView) return null;

  const badge = count > 99 ? "99+" : String(count);
  return (
    <Link
      to="/notifications"
      aria-label={
        count > 0 ? `Notifications, ${count} unseen` : "Notifications"
      }
      className="relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors hover:bg-white/10"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6M10.5 20a1.5 1.5 0 0 0 3 0"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brk-accent px-1 text-[9px] font-bold text-white">
          {badge}
        </span>
      )}
    </Link>
  );
}
